import { useEffect, useRef, useState } from "react";

/**
 * Connects to a MediaStream via AnalyserNode and returns a normalized
 * audio level (0–1) updated each animation frame.
 */
export const useAudioLevel = (stream: MediaStream | undefined): number => {
  const [level, setLevel] = useState(0);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!stream) {
      setLevel(0);
      return;
    }

    const audioContext = new AudioContext();
    audioContext.resume().catch(() => {});

    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateLevel = () => {
      analyser.getByteFrequencyData(dataArray);

      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const normalized = (dataArray[i] ?? 0) / 255;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / dataArray.length);

      setLevel(rms);
      rafIdRef.current = requestAnimationFrame(updateLevel);
    };

    rafIdRef.current = requestAnimationFrame(updateLevel);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      source.disconnect();
      audioContext.close();
    };
  }, [stream]);

  return level;
};
